/**
 * Pure utility for the live-axis ternary plot.
 *
 * Axes (in order of axis index used throughout the codebase):
 *   0 = innocence  → bottom-left vertex  (0, 0)
 *   1 = honneur    → bottom-right vertex (1, 0)
 *   2 = puissance  → top vertex          (0.5, √3/2)
 *
 * A participant's barycentric position is the normalized count of their
 * answers across the 3 axes; we project that to 2D using the equilateral
 * triangle whose vertices are listed above.
 */

const SQRT3_2 = Math.sqrt(3) / 2;
const VERTICES = [
  { x: 0,   y: 0 },        // innocence
  { x: 1,   y: 0 },        // honneur
  { x: 0.5, y: SQRT3_2 },  // puissance
];

/**
 * Count answers per axis from an answers dict {questionIndex: optionIndex}.
 * `questions` is the array from orientation-axis-questions.json. Returns
 * a tuple [innocence, honneur, puissance] of integer counts.
 */
export function axisCounts(answers, questions) {
  const counts = [0, 0, 0];
  if (!answers || !questions) return counts;
  for (const [qIdx, optIdx] of Object.entries(answers)) {
    const q = questions[Number(qIdx)];
    if (!q) continue;
    const opt = q.options[optIdx];
    if (!opt) continue;
    counts[opt.axis]++;
  }
  return counts;
}

/**
 * Compute barycentric coords + 2D projection from an answers dict.
 * Returns null if the participant has not answered anything yet.
 */
export function barycentric(answers, questions) {
  const counts = axisCounts(answers, questions);
  const n = counts[0] + counts[1] + counts[2];
  if (n === 0) return null;
  const bary = [counts[0] / n, counts[1] / n, counts[2] / n];
  return {
    bary,
    counts,
    n,
    x: bary[0] * VERTICES[0].x + bary[1] * VERTICES[1].x + bary[2] * VERTICES[2].x,
    y: bary[0] * VERTICES[0].y + bary[1] * VERTICES[1].y + bary[2] * VERTICES[2].y,
  };
}

/** Euclidean distance between two 2D points {x,y}. */
export function distance(p, q) {
  return Math.hypot(p.x - q.x, p.y - q.y);
}

/** Centroid (mean) of an array of {x,y} points. Returns null if empty. */
export function centroid(points) {
  if (!points || points.length === 0) return null;
  let sx = 0;
  let sy = 0;
  for (const p of points) {
    sx += p.x;
    sy += p.y;
  }
  return { x: sx / points.length, y: sy / points.length };
}

/**
 * Return the N points furthest from `me` (by Euclidean distance), excluding
 * any point that shares `me.label` (so a participant is never their own
 * "furthest"). Each returned entry is the input point augmented with a `d`
 * field carrying the distance.
 */
export function topNFurthest(me, others, n = 2) {
  return others
    .filter((o) => o && o.label !== me.label)
    .map((o) => ({ ...o, d: distance(me, o) }))
    .sort((a, b) => b.d - a.d)
    .slice(0, n);
}

export const TERNARY_VERTICES = VERTICES;
